import { Directive, Input, HostListener, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { debounceTime } from 'rxjs/internal/operators';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[dropdown-max-height]'
})
export class DropdownHeightDirective implements OnInit, OnChanges, OnDestroy {
  @Input() public bottomIndent: number = 12;
  @Input() public parentElement: string;
  @Input() public headTmplOnly: boolean;
  @Input() public headTmplOffset: number = 0;
  @Input() public preserveScrollOnOpen: boolean = false;
  @Input() public fitDropdownToViewport: boolean = false;

  public dropdownId: string;

  private destroy$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private preserveInitialScroll: boolean = false;
  private scrollPositions: {element: HTMLElement; top: number; left: number}[] = [];
  private windowScrollPosition: {top: number; left: number};
  private originalSelectFocus: () => void;
  private preserveScrollVersion: number = 0;

  constructor(private select: NgSelectComponent) {
      this.dropdownId = select.dropdownId;
  }

  private onScroll = (event: any) => {
    if (this.select && this.select.isOpen) {
      if (this.preserveInitialScroll) {
        this.restoreScrollPosition(event.target);
        return;
      }

      const targetClassName = event.target && typeof event.target.className === 'string'
        ? event.target.className
        : '';
      const isScrollingInScrollHost = targetClassName.indexOf('ng-dropdown-panel-items') > -1;
      if (this.headTmplOnly) {
        const isScrollingHead = targetClassName.indexOf('dd-head-list') > -1;
        if (isScrollingHead) { return; }
      }
      if (isScrollingInScrollHost) { return; }
      this.select.close();
    }
  };

  ngOnInit(): void {
    window.addEventListener('scroll', this.onScroll, true);

    if (this.preserveScrollOnOpen) {
      this.overrideSelectFocus();
    }

    if (this.fitDropdownToViewport) {
      this.select.openEvent
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => setTimeout(() => this.getMaxHeight(), 0));
    }

    this.select.searchEvent
      .pipe(
        debounceTime(5),
        takeUntil(this.destroy$)
      )
      .subscribe( e => this.getMaxHeight());
  }

  private overrideSelectFocus(): void {
    this.originalSelectFocus = this.select.focus;
    this.select.focus = () => {
      this.prepareForInitialScroll();

      const searchInput = (this.select as any).searchInput;
      if (!searchInput || !searchInput.nativeElement) {
        this.originalSelectFocus.call(this.select);
      } else {
        let preventScrollSupported = false;
        const focusOptions = Object.defineProperty({}, 'preventScroll', {
          get: () => {
            preventScrollSupported = true;
            return true;
          }
        });

        try {
          searchInput.nativeElement.focus(focusOptions);
        } catch (e) {
          preventScrollSupported = false;
          this.originalSelectFocus.call(this.select);
        }

        if (preventScrollSupported) {
          this.preserveScrollVersion++;
          this.preserveInitialScroll = false;
          return;
        }
      }

      this.restoreAllScrollPositions();
    };
  }

  private prepareForInitialScroll(): void {
    if (!this.preserveScrollOnOpen) { return; }

    const preserveScrollVersion = ++this.preserveScrollVersion;
    this.preserveInitialScroll = true;
    this.windowScrollPosition = {
      top: window.pageYOffset,
      left: window.pageXOffset,
    };
    this.scrollPositions = [];

    let parent = this.select.element.parentElement as HTMLElement;
    while (parent) {
      this.scrollPositions.push({
        element: parent,
        top: parent.scrollTop,
        left: parent.scrollLeft,
      });
      parent = parent.parentElement;
    }

    setTimeout(() => {
      if (preserveScrollVersion === this.preserveScrollVersion) {
        this.restoreAllScrollPositions();
      }
    }, 0);
    setTimeout(() => {
      if (preserveScrollVersion !== this.preserveScrollVersion) { return; }

      this.restoreAllScrollPositions();
      this.preserveInitialScroll = false;
    }, 150);
  }

  private restoreScrollPosition(target: EventTarget): void {
    const savedPosition = this.scrollPositions.find(item => item.element === target);
    if (savedPosition) {
      savedPosition.element.scrollTop = savedPosition.top;
      savedPosition.element.scrollLeft = savedPosition.left;
      return;
    }

    if (target === document || target === window) {
      this.restoreWindowScrollPosition();
    }
  }

  private restoreAllScrollPositions(): void {
    this.scrollPositions.forEach(item => {
      item.element.scrollTop = item.top;
      item.element.scrollLeft = item.left;
    });
    this.restoreWindowScrollPosition();
  }

  private restoreWindowScrollPosition(): void {
    if (!this.windowScrollPosition) { return; }

    window.scrollTo(this.windowScrollPosition.left, this.windowScrollPosition.top);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('headTmplOnly') || changes.hasOwnProperty('bottomIndent')) {
      // timeout for header template update
      setTimeout(() =>  this.getMaxHeight(), 0);
    }
  }

  @HostListener('click', ['$event'])
  onClick() {
    this.getMaxHeight();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    if (this.select && this.select.isOpen) {
      this.select.close();
    }
  }

  public getMaxHeight() {
    const panel = document.querySelector(`#${this.dropdownId}`);
    const dropdownHeader = panel && panel.querySelector('.ng-dropdown-header');
    const dropdownFooter = panel && panel.querySelector('.ng-dropdown-footer');
    const dropdownContainer = panel && panel.querySelector('.ng-dropdown-panel-items') as HTMLElement;
    const bodyHeight = this.fitDropdownToViewport
      ? window.innerHeight || document.documentElement.clientHeight
      : document.body.getBoundingClientRect().height;
    let {top, height} = this.select.element.getBoundingClientRect();
    let parentElementHeight;

    if (this.parentElement) {
      const parentElementContainer = document.querySelector(this.parentElement).getBoundingClientRect();
      parentElementHeight = parentElementContainer.height;
      top = top - parentElementContainer.top;
    } else {
      parentElementHeight = bodyHeight;
    }


    const dropdownPanelTop = top + height;
    const dropdownPanelMaxHeightToBottom = (parentElementHeight - dropdownPanelTop) - this.bottomIndent;
    const dropdownPanelMaxHeightToTop = top - this.bottomIndent;
    let dropdownPanelMaxHeight;

    if (this.select.currentPanelPosition === 'bottom') {
      dropdownPanelMaxHeight = dropdownPanelMaxHeightToBottom;
    } else {
      dropdownPanelMaxHeight = dropdownPanelMaxHeightToTop;
    }

    if (this.fitDropdownToViewport && panel) {
      const panelStyles = window.getComputedStyle(panel);
      dropdownPanelMaxHeight -= parseFloat(panelStyles.paddingTop) || 0;
      dropdownPanelMaxHeight -= parseFloat(panelStyles.paddingBottom) || 0;
    }

    if (this.fitDropdownToViewport && dropdownFooter) {
      dropdownPanelMaxHeight -= this.getOuterHeight(dropdownFooter);
    }

    if  (dropdownContainer) {
      dropdownContainer.classList.remove('m-0', 'p-0');
    }

    if (dropdownHeader) {
      if (this.headTmplOnly) {
        const headerContainer = document.querySelector('.ng-dropdown-header') as HTMLElement;
        const headerList = document.querySelector('.dd-head-list') as HTMLElement;
        if (headerList) {
          headerList.style.maxHeight = `${dropdownPanelMaxHeight - this.headTmplOffset }px`;
        }
        headerContainer.style.maxHeight = `${dropdownPanelMaxHeight}px`;
        dropdownContainer.classList.add('m-0', 'p-0');
        return;
      }

      dropdownPanelMaxHeight -= this.fitDropdownToViewport
        ? this.getOuterHeight(dropdownHeader)
        : dropdownHeader.getBoundingClientRect().height;
    }

    if (panel && dropdownContainer) {
      dropdownContainer.style.maxHeight = this.fitDropdownToViewport
        ? `${Math.max(0, dropdownPanelMaxHeight)}px`
        : `${dropdownPanelMaxHeight}px`;
    }
  }

  private getOuterHeight(element: Element): number {
    const styles = window.getComputedStyle(element);
    const marginTop = parseFloat(styles.marginTop) || 0;
    const marginBottom = parseFloat(styles.marginBottom) || 0;

    return element.getBoundingClientRect().height + marginTop + marginBottom;
  }

  ngOnDestroy() {
    if (this.originalSelectFocus) {
      this.select.focus = this.originalSelectFocus;
    }
    this.preserveScrollVersion++;
    window.removeEventListener('scroll', this.onScroll, true);
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
